<template>
  <!-- DetailItem -->
  <detail-item
    v-bind="$attrs"
    :name-spacing="nameSpacing"
    :with-input="(mode === 'add' && !!firstDateInRule) || !hasTxtBeforeBtns ? 31 : undefined"
    name="Recurring Rule"
    icon="mdi-update"
  >
    <page-banner
      v-if="mode === 'add' && !!firstDateInRule"
      :color="ruleBeginsOnStartDate ? 'green-7' : 'orange-10'"
      :icon="!ruleBeginsOnStartDate ? 'mdi-exclamation' : undefined"
      dense
      dark
      class="q-mb-sm"
    >
      <q-item-label class="text-bold">
        The rule will begin on {{ $formatDate(firstDateInRule, luxonFormats.SHORT_WORDED_DATE) }}.
      </q-item-label>
      <q-item-label v-if="!ruleBeginsOnStartDate" class="text-caption">
        Please ensure it doesn't conflict with this appointment date to avoid extra unwanted appointments.
      </q-item-label>
    </page-banner>

    <template v-if="!hasFutureRule && can(permissions.isSubscribed)">
      <div
        v-if="recurringRule"
        class="q-pb-sm"
      >
        <q-item-label class="text-bold">
          <span v-if="mode !== 'add'">This appointment is part of a recurring rule which occurs</span>
          <span v-else>This recurring rule will occur</span>
          <span>&nbsp;{{ recurringRule.toText() }}</span>
        </q-item-label>
        <q-item-label v-if="mode === 'add'">
          {{ selectedRecurringStatus.label }} appointments will be created.
        </q-item-label>
      </div>

      <div>
        <q-btn
          size="12px"
          color="green"
          :label="(recurringRule ? 'Update Rule' : 'Create Recurring Rule')"
          :icon="recurringRule ? 'mdi-pencil' : 'mdi-plus'"
          @click="$refs.rruleDialog.open(event.recursion_rule ? event.recursion_rule.value : null)"
        />
        <q-btn
          v-if="recurringRule"
          size="12px"
          color="red-7"
          flat
          label="Remove Rule"
          @click="onRemoveRRule"
        />
      </div>
    </template>
    <template v-else-if="!can(permissions.isSubscribed)">
      <page-banner
        dense
        label="Creating recurring rules is a subscription-only feature."
      />
    </template>

    <!-- Banners -->
    <template v-if="mode === 'edit'">
      <page-banner
        v-if="!hasUpdatedRule && hasUpdatedStartDate && !mustUpdateRuleFromDate && !!event.recursion_rule"
        dense
        class="q-my-sm"
      >
        <q-item-label>
          Updating the start date will <b>not</b> update recurring appointments.
        </q-item-label>
        <q-item-label>
          You may optionally update recurring appointments too.
        </q-item-label>
        <q-item-label class="text-bold">
          By enabling this, any appointments after this appointment date will be deleted and then recreated after when you click 'Save'.
        </q-item-label>
        <template #action>
          <q-btn
            size="12px"
            color="white"
            text-color="black"
            label="Update Recurring Appointments too"
            @click="bindRuleToDate"
          />
        </template>
      </page-banner>
      <page-banner
        v-if="hasUpdatedRule"
        dense
        color="green-2"
        class="q-my-sm"
        icon="mdi-exclamation"
        label="Recurring appointments will be recreated with the reflected changes."
      />
      <page-banner
        v-if="hasDeletedRule"
        dense
        color="red-2"
        class="q-my-sm"
        icon="mdi-exclamation"
        label="Recurring appointments will be deleted."
      />
      <page-banner
        v-if="hasFutureRule"
        dense
        class="q-my-sm"
        icon="mdi-exclamation"
        label="The recurring rule has been changed via another future recurring appointment. It can not be changed from this appointment."
      />
    </template>
  </detail-item>

  <rrule-dialog
    ref="rruleDialog"
    :with-estimates="true"
    :starting-from="event.start_date"
    :with-estimated-time="event.recurring_status !== 'UNSCHEDULED' && !event.all_day"
    @save="onSaveRRule"
    @remove="onRemoveRRule"
  >
    <q-item-wrapper>
      <q-item-label class="text-bold q-pb-sm">
        Mark recurring appointments as:
      </q-item-label>
      <div class="q-mb-sm">
        <q-btn-toggle
          v-model="event.recurring_status"
          :options="availableRecurringStatusOptions"
          :disable="event.status === 'UNSCHEDULED'"
          no-caps
          size="12px"
        />
      </div>
    </q-item-wrapper>
  </rrule-dialog>
</template>

<script>
import { RRule } from 'rrule'
import rruleMixin from 'components/partials/rrule/rruleMixin'
import statusMixin from 'components/app/calendar/partials/mixins/statusMixin'

export default {
  mixins: [rruleMixin, statusMixin],
  props: {
    event: {
      required: true,
      type: Object
    },
    startDate: {
      type: String
    },
    startTime: {
      type: String
    },
    mode: {
      type: String,
      default: 'add'
    },
    hasUpdatedRule: {
      type: Boolean,
      default: false
    },
    hasDeletedRule: {
      type: Boolean,
      default: false
    },
    hasFutureRule: {
      type: Boolean,
      default: false
    },
    nameSpacing: {
      type: Number
    }
  },
  data () {
    return {
      hasUpdatedStartDate: false,
      // Should be initially true if a recurring rule doesn't exist
      // So that if creating a rule when editing an appointment, changing the date will
      // automatically change the rule
      mustUpdateRuleFromDate: !this.event.recursion_rule
    }
  },
  computed: {
    recurringRule () {
      try {
        return RRule.fromString(this.event.recursion_rule.value)
      }
      catch (err) {
        return null
      }
    },
    selectedRecurringStatus () {
      return this.baseStatusOptions(true).find(item => item.value === this.event.recurring_status)
    },
    availableRecurringStatusOptions () {
      return this.baseStatusOptions(true).filter(item =>
        item.value === 'SCHEDULED' || item.value === 'UNSCHEDULED' || item.value === 'UNCONFIRMED'
      )
    },
    firstDateInRule () {
      if (!this.recurringRule) {
        return null
      }
      let firstDate = null
      this.recurringRule.all((date) => {
        // Set zone to UTC for displaying because it attempts to set timezone to AEST with conversion
        firstDate = this.luxon.fromJSDate(date, { zone: 'UTC' }).toSimpleSQL()

        // Return false to stop iteration after first element
        return false
      })
      return firstDate
    },
    ruleBeginsOnStartDate () {
      return !this.firstDateInRule || this.luxon.fromSQL(this.firstDateInRule).hasSame(this.luxon.fromSQL(this.event.start_date), 'day')
    },
    hasTxtBeforeBtns () {
      return !this.hasFutureRule && this.can(this.permissions.isSubscribed) && !!this.recurringRule
    }
  },
  watch: {
    'startDate' () {
      if (this.mode === 'edit' && !this.hasUpdatedStartDate) {
        this.hasUpdatedStartDate = true
      }
      this.updateRuleFromDate()
    },
    'startTime' () {
      if (this.mode === 'edit') {
        this.hasUpdatedStartDate = true
      }
    }
  },
  methods: {
    onSaveRRule (str) {
      if (!this.event.recursion_rule) {
        this.event.recursion_rule = {
          value: str
        }
      }
      else {
        this.event.recursion_rule.value = str
      }
    },
    updateRuleFromDate () {
      if (this.mode !== 'edit' || this.mustUpdateRuleFromDate) {
        if (this.event.recursion_rule) {
          try {
            this.event.recursion_rule.value = this.createValidatedRrule(this.event.recursion_rule.value, this.startDate)
          }
          catch (err) {
            console.log(err)
            this.event.recursion_rule = null
          }
        }
      }
    },
    bindRuleToDate () {
      this.mustUpdateRuleFromDate = true
      this.updateRuleFromDate()
    },
    onRemoveRRule () {
      this.event.recursion_rule = null
    }
  }
}
</script>
